import { Observable } from 'rxjs'
import { normalize, schema } from 'normalizr'

import { request, parseAPIError } from '../../common/api'
import { costSchema } from '../../common/normalizrSchemas'
import { normalizedEntitiesToRecordMap } from '../../common/helpers'

import * as actionTypes from './actionTypes'
import * as actionCreators from './actionCreators'
import * as tableModelActionTypes from '../../common/table/actionTypes'
import * as tableModelActionCreators from '../../common/table/actionCreators'
import { resolveModelState } from '../../common/table/helpers'
import { Cost } from './model'

import { CostType } from '../cost_types/model'
import * as costTypesActionCreators from '../cost_types/actionCreators'
import { Currency } from '../currencies/model'
import * as currenciesActionCreators from '../currencies/actionCreators'
import { CompanyCostCenter } from '../company_cost_centers/model'
import * as companyCostCentersActionCreators from '../company_cost_centers/actionCreators'
import { User } from '../users/model'
import * as usersActionCreators from '../users/actionCreators'
import { Driver } from '../drivers/model'
import * as driversActionCreators from '../drivers/actionCreators'
import { Vehicle } from '../vehicles/model'
import * as vehiclesActionCreators from '../vehicles/actionCreators'
import { Trailer } from '../trailers/model'
import * as trailersActionCreators from '../trailers/actionCreators'
import { fromJS } from "immutable";

// Fetch

const fetchCostsEpic = (action$, store) => {
    const tableIdentifier = 'costs_list'
    const serverSide = true

    const triggeringAction = serverSide
        ? action$.ofType(actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED)
        : action$.ofType(actionTypes.FETCH)

    return triggeringAction
        .filter(action => action.type !== tableModelActionTypes.CONFIGURATION_CHANGED || action.payload.tableIdentifier === tableIdentifier)
        .switchMap(action => {
            const modelState = resolveModelState(tableIdentifier, store.getState(), action)

            const requestParams = {
                method: 'GET',
                path: `costs/list`,
            }

            if (serverSide) {
                requestParams.method = 'POST'
                requestParams.path += `?page=${modelState.getIn(['pagination', 'current']) + 1}`
                requestParams.body = {
                    sorting: modelState.get('sorting').toJS(),
                    filters: modelState.get('filters').toJS(),
                }
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_STARTED,
                }),
                request(requestParams)
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(
                            serverSide ? ajaxResponse.response.data : ajaxResponse.response,
                            new schema.Array(costSchema)
                        )

                        const costs = normalizedEntitiesToRecordMap(normalizedData.entities.costs, Cost, normalizedData.result)
                        const costTypes = normalizedEntitiesToRecordMap(normalizedData.entities.cost_types, CostType)
                        const currencies = normalizedEntitiesToRecordMap(normalizedData.entities.currencies, Currency)
                        const company_cost_center = normalizedEntitiesToRecordMap(normalizedData.entities.company_cost_centers, CompanyCostCenter)
                        const drivers = normalizedEntitiesToRecordMap(normalizedData.entities.drivers, Driver)
                        const users = normalizedEntitiesToRecordMap(normalizedData.entities.users, User)
                        const vehicles = normalizedEntitiesToRecordMap(normalizedData.entities.vehicles, Vehicle)
                        const trailers = normalizedEntitiesToRecordMap(normalizedData.entities.trailers, Trailer)

                        const observables = [
                            Observable.of(actionCreators.fetchCostsFulfilled(costs)),
                            Observable.of(costTypesActionCreators.fetchCostTypesFulfilled(costTypes)),
                            Observable.of(currenciesActionCreators.fetchCurrenciesFulfilled(currencies)),
                            Observable.of(companyCostCentersActionCreators.fetchCompanyCostCentersFulfilled(company_cost_center)),
                            Observable.of(driversActionCreators.fetchDriversFulfilled(drivers)),
                            Observable.of(usersActionCreators.fetchUsersFulfilled(users)),
                            Observable.of(vehiclesActionCreators.fetchVehiclesFulfilled(vehicles)),
                            Observable.of(trailersActionCreators.fetchTrailersFulfilled(trailers)),
                        ]

                        serverSide &&
                            observables.push(
                                Observable.of(
                                    tableModelActionCreators.updatePagination(
                                        tableIdentifier,
                                        ajaxResponse.response.last_page,
                                        ajaxResponse.response.current_page - 1,
                                        ajaxResponse.response.total
                                    )
                                )
                            )

                        return Observable.concat(...observables)
                    })
                    .catch(error => Observable.of(actionCreators.fetchCostsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_CANCELLED, actionTypes.FETCH, tableModelActionTypes.CONFIGURATION_CHANGED))
            )
        })
}

// Fetch one

const fetchCostEpic = (action$, store) =>
    action$
        .ofType(actionTypes.FETCH_ONE)
        .filter(() => !store.getState().costs.getIn(['current', 'fetching']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_ONE_STARTED,
                }),
                request({
                    path: `costs/${action.payload}`,
                    method: 'GET',
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, costSchema)

                        const costs = normalizedEntitiesToRecordMap(normalizedData.entities.costs, Cost)

                        return Observable.concat(
                            Observable.of(actionCreators.fetchCostsFulfilled(costs)),
                            Observable.of(actionCreators.fetchCostFulfilled())
                        )
                    })
                    .catch(error => Observable.of(actionCreators.fetchCostsRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.FETCH_ONE_CANCELLED))
            )
        )

// Save

const saveCostEpic = (action$, store) =>
    action$
        .ofType(actionTypes.SAVE)
        .filter(() => !store.getState().costs.getIn(['current', 'saving']))
        .switchMap(action => {
            // eslint-disable-next-line prefer-object-spread
            const values = Object.assign({}, action.payload)

            let path = `costs`
            let method = 'POST'

            if (values.id) {
                path = `costs/${values.id}`
                method = 'PUT'

                delete values.id
            }

            return Observable.concat(
                Observable.of({
                    type: actionTypes.SAVE_STARTED,
                }),
                request({
                    path,
                    method,
                    body: values,
                })
                    .switchMap(ajaxResponse => {
                        const normalizedData = normalize(ajaxResponse.response, costSchema)

                        const costs = normalizedEntitiesToRecordMap(normalizedData.entities.costs, Cost)
                        const cost = costs.valueSeq().first()

                        return Observable.concat(Observable.of(actionCreators.saveCostFulfilled(cost)))
                    })
                    .catch(error => Observable.of(actionCreators.saveCostRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.SAVE_CANCELLED))
            )
        })

// Delete

const deleteCostEpic = (action$, store) =>
    action$
        .ofType(actionTypes.DELETE)
        .filter(() => !store.getState().costs.getIn(['deletion', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.DELETE_STARTED,
                }),
                request({
                    path: `costs/${action.payload.id}${action.payload.withFuture ? '/with-future' : ''}`,
                    method: 'DELETE',
                })
                    .switchMap(() => Observable.of(actionCreators.deleteCostFulfilled(action.payload.id)))
                    .catch(error => Observable.of(actionCreators.deleteCostRejected(parseAPIError(error))))
                    .takeUntil(action$.ofType(actionTypes.DELETE_CANCELLED))
            )
        )

// Fetch import headers

const fetchImportHeadersEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.FETCH_IMPORT_HEADERS)
        .filter(() => !store.getState().costs.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_IMPORT_HEADERS_STARTED,
                }),
                request({
                    path: `costs/import/get-headers`,
                    method: 'POST',
                    body: {
                        file: action.payload,
                    },
                })
                .switchMap(ajaxResponse => {
                    const importHeaders = {
                        file_id: ajaxResponse.response.file_id,
                        headers: fromJS(ajaxResponse.response.headers),
                        headers_default: fromJS(ajaxResponse.response.headers_default),
                    }
                    return Observable.of(actionCreators.fetchCostsImportHeadersFulfilled(importHeaders))
                })
                .catch(error => Observable.of(actionCreators.fetchCostsImportHeadersRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.FETCH_IMPORT_HEADERS_CANCELLED))
            )
        )
}

// Fetch import items

const fetchImportItemsEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.FETCH_IMPORT_ITEMS)
        .filter(() => !store.getState().costs.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.FETCH_IMPORT_ITEMS_STARTED,
                }),
                request({
                    path: `costs/import/get-items`,
                    method: 'POST',
                    body: {
                        fileId: action.payload.fileId,
                        columnTypes: action.payload.columnTypes,
                        defaultDate: action.payload.defaultDate,
                        defaultCostTypeId: action.payload.defaultCostTypeId,
                    },
                })
                .switchMap(ajaxResponse => {
                    return Observable.of(actionCreators.fetchCostsImportItemsFulfilled(ajaxResponse.response.items, ajaxResponse.response.defaultDate, ajaxResponse.response.defaultCostTypeId))
                })
                .catch(error => Observable.of(actionCreators.fetchCostsImportItemsRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.FETCH_IMPORT_ITEMS_CANCELLED))
            )
        )
}

// Import costs

const importCostsEpic = (action$, store) => {
    return action$
        .ofType(actionTypes.IMPORT_COSTS)
        .filter(() => !store.getState().costs.getIn(['import', 'inProgress']))
        .switchMap(action =>
            Observable.concat(
                Observable.of({
                    type: actionTypes.IMPORT_COSTS_STARTED,
                }),
                request({
                    path: `costs/import/import`,
                    method: 'POST',
                    body: {
                        fileId: action.payload.fileId,
                        columnTypes: action.payload.columnTypes,
                        defaultDate: action.payload.defaultDate,
                        defaultCostTypeId: action.payload.defaultCostTypeId,
                        keys: action.payload.keys,
                    },
                })
                .switchMap(ajaxResponse => {
                    return Observable.of(actionCreators.importCostsFulfilled())
                })
                .catch(error => Observable.of(actionCreators.importCostsRejected(parseAPIError(error))))
                .takeUntil(action$.ofType(actionTypes.IMPORT_COSTS_CANCELLED))
            )
        )
}

export default [fetchCostsEpic, fetchCostEpic, saveCostEpic, deleteCostEpic, fetchImportHeadersEpic, fetchImportItemsEpic, importCostsEpic]
